/**
 * Onboarding Settings Config
 *
 * Declarative per-platform "discover / confirm" field definitions, consumed
 * by <OnboardingSettingsPanel />. To support a new ad platform, add a new
 * entry here (and, if needed, a small amount of custom option-building logic
 * for that platform's option shapes) -- the panel itself stays generic.
 *
 * Field types:
 *   'select'   - Dropdown built from `options[optionsKey]`, using
 *                buildOptions(rawOptions) => [{ value, label }]
 *   'readonly' - Discovered value displayed as text (no user choice),
 *                optionally enriched via buildLabel(discovered, options)
 *   'hidden'   - Included in the confirm payload automatically from the
 *                discovered values, but not rendered to the user (e.g.
 *                technical/internal IDs with no user-facing meaning)
 */

const metaFields = [
  {
    key: 'businessId',
    label: 'Business',
    type: 'select',
    optionsKey: 'adAccounts',
    // A Meta account only ever has one business, so this dropdown isn't
    // meant to be cleared/blanked out like the others.
    isClearable: false,
    // No standalone `options.businesses` array is provided -- the business
    // is nested under each ad account, so derive the (usually single)
    // business as a dropdown, deduped by business id.
    buildOptions: (adAccounts = []) => {
      const seen = new Set();
      return adAccounts
        .filter((acc) => {
          if (!acc.business?.id || seen.has(acc.business.id)) return false;
          seen.add(acc.business.id);
          return true;
        })
        .map(acc => ({
          value: acc.business.id,
          label: `${acc.business.name} (${acc.business.id})`,
        }));
    },
  },
  {
    key: 'adAccountId',
    label: 'Ad Account',
    type: 'select',
    optionsKey: 'adAccounts',
    buildOptions: (adAccounts = []) => adAccounts.map(acc => ({
      value: String(acc.id).replace(/^act_/, ''),
      label: `${acc.name} (${acc.id})`,
    })),
  },
  {
    key: 'pageId',
    label: 'Facebook Page',
    type: 'select',
    optionsKey: 'pages',
    buildOptions: (pages = []) => pages.map(page => ({
      value: page.id,
      label: `${page.name} (${page.id})`,
    })),
  },
  {
    key: 'instagramAccountId',
    label: 'Instagram Account',
    type: 'select',
    optionsKey: 'pages',
    // Instagram accounts aren't returned as their own list -- they hang off
    // each Page. Build a standalone dropdown from that nested data, since
    // Meta itself treats Page and Instagram as separate selections. Label
    // uses the Facebook page name it was found on, with the IG account id
    // in parens (Instagram accounts don't carry their own name here).
    buildOptions: (pages = []) => pages
      .filter(page => page.instagram_business_account?.id)
      .map(page => ({
        value: page.instagram_business_account.id,
        label: `${page.name} (${page.instagram_business_account.id})`,
      })),
  },
  {
    key: 'pixelId',
    label: 'Pixel',
    type: 'select',
    optionsKey: 'pixels',
    buildOptions: (pixels = []) => pixels.map(pixel => ({
      value: pixel.id,
      label: `${pixel.name} (${pixel.id})`,
    })),
  },
  {
    key: 'catalogId',
    label: 'Catalog',
    type: 'select',
    optionsKey: 'catalogs',
    buildOptions: (catalogs = []) => catalogs.map(catalog => ({
      value: catalog.id,
      label: `${catalog.name} (${catalog.id})`,
    })),
  },
  {
    key: 'systemUserId',
    label: 'System User',
    type: 'hidden',
  },
];

export const ONBOARDING_SETTINGS_CONFIG = {
  meta: {
    fields: metaFields,
    intro: 'We found the following settings connected to your Meta Business account. Review the selections below and confirm to finish connecting your account.',
  },
};

/**
 * Get the onboarding settings config for a given ad platform.
 * @param {string} platform - Platform key (e.g., 'meta')
 * @returns {object|null}
 */
export const getOnboardingSettingsConfig = (platform) => {
  if (!platform) return null;
  return ONBOARDING_SETTINGS_CONFIG[platform.toLowerCase()] || null;
};
